import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, Info, AlertTriangle } from "lucide-react";

// Centered, full-screen ephemeral messages with the APP PRESSE icon — replaces
// the old top toasts that collided with the phone camera / notch (Dynamic Island).
// Keeps the same `toast.success/error/info/message` API so no call site changes.

const FLASH_EVENT = "appresse:flash";
const DURATION = 4000;

function emit(type, message, duration) {
  if (typeof window === "undefined" || message == null) return;
  window.dispatchEvent(new CustomEvent(FLASH_EVENT, { detail: { type, message: String(message), duration } }));
}

const dur = (o) => (o && typeof o.duration === "number" ? o.duration : undefined);

export const toast = Object.assign((m, o) => emit("message", m, dur(o)), {
  success: (m, o) => emit("success", m, dur(o)),
  error: (m, o) => emit("error", m, dur(o)),
  info: (m, o) => emit("info", m, dur(o)),
  warning: (m, o) => emit("warning", m, dur(o)),
  message: (m, o) => emit("message", m, dur(o)),
  dismiss: () => {},
  loading: (m, o) => emit("message", m, dur(o)),
});

const ICONS = {
  success: <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />,
  error: <XCircle className="w-4 h-4 text-red-500 shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
  info: <Info className="w-4 h-4 text-primary shrink-0" />,
  message: null,
};

let SEQ = 0;

export function Toaster() {
  const [items, setItems] = useState([]);

  const remove = useCallback((id) => {
    setItems((list) => list.filter((x) => x.id !== id));
  }, []);

  useEffect(() => {
    const onFlash = (e) => {
      const id = ++SEQ;
      const { type, message } = e.detail || {};
      setItems((list) => [...list.slice(-2), { id, type: type || "message", message }]);
      // Toutes les notifications restent 4 secondes à l'écran (durée uniforme).
      setTimeout(() => remove(id), DURATION);
    };
    window.addEventListener(FLASH_EVENT, onFlash);
    return () => window.removeEventListener(FLASH_EVENT, onFlash);
  }, [remove]);

  if (!items.length) return null;

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none flex items-center justify-center px-6"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="flash-host">
      <div className="flex flex-col items-center gap-3">
        {items.map((t) => (
          <button key={t.id} type="button" onClick={() => remove(t.id)}
            data-testid="flash-message" data-flash-type={t.type}
            className="pointer-events-auto w-[min(88vw,340px)] bg-white dark:bg-slate-900 rounded-3xl shadow-[0_24px_70px_-12px_rgba(0,0,0,0.55)] border border-slate-200/70 dark:border-slate-700 px-6 py-5 flex flex-col items-center text-center animate-in fade-in zoom-in-95 duration-200">
            <img src="/icon-192.png" alt="APP PRESSE" className="w-14 h-14 rounded-2xl mb-3 shadow-sm" />
            <div className="flex items-center justify-center gap-1.5">
              {ICONS[t.type]}
              <p className="font-semibold text-sm leading-snug text-slate-900 dark:text-slate-100">{t.message}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
